import React, { useState, useRef } from 'react';
import { Drawer, Button, Textarea, MessagePlugin } from 'tdesign-react';
import { Form } from '../../../ui/universe';
import '../../../../style/RecognizeAgent.css';
import '../../../../style/AssistMarkType.css';

// ─── 工具函数 ─────────────────────────────────────────────────────────────────

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function stripPrefix(base64) {
  return base64.replace(/^data:.+?;base64,/, '');
}

function createEmptyQA() {
  return { id: Date.now() + Math.random(), Question: '', Answer: '', Analysis: '', Score: '' };
}

// ─── 子组件：单题文本辅助（AnswerAssistMap）编辑弹窗 ─────────────────────────

function AnswerAssistDialog({ visible, initialItems, onConfirm, onCancel }) {
  const [items, setItems] = useState(() =>
    initialItems && initialItems.length > 0
      ? initialItems.map((it, i) => ({ id: i, ...it }))
      : [createEmptyQA()]
  );

  const update = (id, field, val) =>
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, [field]: val } : it)));

  const remove = (id) => {
    if (items.length <= 1) return;
    setItems((prev) => prev.filter((it) => it.id !== id));
  };

  const handleConfirm = () => {
    const valid = items.filter((it) => it.Question.trim() || it.Answer.trim());
    if (valid.length === 0) {
      MessagePlugin.warning('请至少填写一道题的题干或答案');
      return;
    }
    onConfirm(
      valid.map(({ Question, Answer, Analysis, Score }) => ({
        QuestionAuxStem: Question,
        ReferenceAnswer: Answer,
        ...(Analysis ? { Analysis } : {}),
        ...(Score !== '' ? { Score } : {}),
      }))
    );
  };

  return (
    <Drawer
      header="单题文本辅助（AnswerAssistMap）"
      visible={visible}
      onClose={onCancel}
      size="560px"
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="outline" onClick={onCancel}>取消</Button>
          <Button theme="primary" onClick={handleConfirm}>完成</Button>
        </div>
      }
    >
      <div className="assist-mark-dialog__body">
        {items.map((item, index) => (
          <div key={item.id} className="assist-mark-qa-item">
            <div className="assist-mark-qa-item__header">
              <span className="assist-mark-qa-item__index">{`Q${index + 1}`}</span>
              <input
                className="assist-mark-qa-item__name"
                placeholder={`题目 ${index + 1}`}
                defaultValue={`Q${index + 1}`}
                readOnly
              />
              <span
                className="assist-mark-qa-item__remove"
                onClick={() => remove(item.id)}
              >
                ⊗ 删除
              </span>
            </div>
            <div className="assist-mark-qa-item__fields">
              <div className="assist-mark-qa-field">
                <div className="assist-mark-qa-field__label">题干内容（Question）</div>
                <Textarea
                  placeholder="例：计算 (12 + 8) × 3 = ？"
                  value={item.Question}
                  autosize={{ minRows: 3, maxRows: 6 }}
                  onChange={(v) => update(item.id, 'Question', v)}
                />
              </div>
              <div className="assist-mark-qa-field">
                <div className="assist-mark-qa-field__label">标准答案（Answer）</div>
                <Textarea
                  placeholder="例：60"
                  value={item.Answer}
                  autosize={{ minRows: 3, maxRows: 6 }}
                  onChange={(v) => update(item.id, 'Answer', v)}
                />
              </div>
              <div className="assist-mark-qa-field">
                <div className="assist-mark-qa-field__label">解析（可选）</div>
                <Textarea
                  placeholder="先算括号内加法，再乘 3"
                  value={item.Analysis}
                  autosize={{ minRows: 2, maxRows: 4 }}
                  onChange={(v) => update(item.id, 'Analysis', v)}
                />
              </div>
              <div className="assist-mark-qa-field">
                <div className="assist-mark-qa-field__label">分值（可选）</div>
                <Textarea
                  placeholder="例：5"
                  value={item.Score}
                  autosize={{ minRows: 1, maxRows: 2 }}
                  onChange={(v) => update(item.id, 'Score', v)}
                />
              </div>
            </div>
          </div>
        ))}
        <div
          className="assist-mark-dialog__add"
          onClick={() => setItems((prev) => [...prev, createEmptyQA()])}
        >
          + 新增题目
        </div>
      </div>
    </Drawer>
  );
}

// ─── 子组件：标准答案图片（ImageBase64List）上传弹窗 ──────────────────────────

function ImageListDialog({ visible, initialImages, onConfirm, onCancel }) {
  // images: [{id, previewUrl, base64}]
  const [images, setImages] = useState(() => initialImages || []);
  const fileInputRef = useRef(null);

  const handleFiles = async (files) => {
    const ACCEPT = ['jpg', 'jpeg', 'png', 'bmp', 'pdf'];
    const MAX_MB = 10;
    for (const file of files) {
      const ext = file.name.split('.').pop().toLowerCase();
      if (!ACCEPT.includes(ext)) {
        MessagePlugin.warning(`不支持 ${ext} 格式，请上传 ${ACCEPT.join('/')} 图片`);
        continue;
      }
      const raw = await fileToBase64(file);
      if (raw.length > MAX_MB * 1024 * 1024) {
        MessagePlugin.warning(`${file.name} 超过 ${MAX_MB}M 限制`);
        continue;
      }
      setImages((prev) => [
        ...prev,
        { id: `${Date.now()}-${Math.random()}`, previewUrl: raw, base64: stripPrefix(raw) },
      ]);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleFiles(Array.from(e.dataTransfer.files));
  };

  const handleConfirm = () => {
    if (images.length === 0) {
      MessagePlugin.warning('请至少上传一张答案图片');
      return;
    }
    onConfirm(images.map((img) => img.base64));
  };

  return (
    <Drawer
      header="标准答案图片（ImageBase64List.N）"
      visible={visible}
      onClose={onCancel}
      size="560px"
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="outline" onClick={onCancel}>取消</Button>
          <Button theme="primary" onClick={handleConfirm}>完成</Button>
        </div>
      }
    >
      <div className="assist-mark-dialog__body">
        {/* 上传区 */}
        <div
          className="assist-mark-img-dropzone"
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".jpg,.jpeg,.png,.bmp,.pdf"
            style={{ display: 'none' }}
            onChange={(e) => {
              handleFiles(Array.from(e.target.files));
              e.target.value = '';
            }}
          />
          <span className="assist-mark-img-dropzone__icon" />
          <div className="assist-mark-img-dropzone__title">上传答案图片或答案 PDF（可多页多张）</div>
          <div className="assist-mark-img-dropzone__desc">
            非答题卡场景：请上传标准答案整页图，多张按 List 顺序对应
          </div>
        </div>

        {/* 已上传列表 */}
        <div className="assist-mark-img-section-title">
          <span>已上传答案图片</span>
          <span className="assist-mark-img-section-count">
            {images.length > 0 ? `${images.length} 张` : '尚未上传'}
          </span>
        </div>
        {images.length === 0 ? (
          <div className="assist-mark-img-empty">
            <span className="assist-mark-img-empty__icon" />
            <span>暂无答案图片</span>
          </div>
        ) : (
          <div className="assist-mark-img-grid">
            {images.map((img, idx) => (
              <div key={img.id} className="assist-mark-img-cell">
                <img src={img.previewUrl} alt={`答案${idx + 1}`} className="assist-mark-img-cell__img" />
                <div className="assist-mark-img-cell__label">第 {idx + 1} 张</div>
                <span
                  className="assist-mark-img-cell__remove"
                  onClick={() => setImages((prev) => prev.filter((_, i) => i !== idx))}
                >
                  ×
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Drawer>
  );
}

//─── 主组件 ──────────────────────────────────────────────────────────────────

// ─── 辅助：解析 AnswerAssistMap JSON 字符串为内部数组 ─────────────────────────

function parseAnswerAssistMap(raw) {
  if (!raw) return [];
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    // API 示例是单个对象，也兼容数组
    return Array.isArray(parsed) ? parsed : [parsed];
  } catch {
    return [];
  }
}

// ─── 主组件 ──────────────────────────────────────────────────────────────────

function AssistMarkTypeRenderer({ params, value, onChange, allParamsValue, onParamsChange }) {
  const [qaDialogOpen, setQaDialogOpen] = useState(false);
  const [imgDialogOpen, setImgDialogOpen] = useState(false);

  const options = params.options || [];

  // AnswerAssistMap 在 paramsValue 中以 JSON 字符串存储，ImageBase64List 以数组存储
  const answerAssistMapRaw = allParamsValue?.AnswerAssistMap || null;
  const imageBase64List = allParamsValue?.ImageBase64List || null;

  // 解析出内部数组（用于 UI 显示计数、初始化抽屉表单）
  const answerAssistItems = parseAnswerAssistMap(answerAssistMapRaw);
  const qaCount = answerAssistItems.length;
  const imgCount = Array.isArray(imageBase64List) ? imageBase64List.length : 0;

  // 切换批改类型时清除另一种辅助数据
  const handleTypeChange = (newType) => {
    onParamsChange && onParamsChange({ AnswerAssistMap: null, ImageBase64List: null });
    onChange && onChange(newType, newType, options.find((o) => o.value === newType));
  };

  // type=1 配置确认：序列化为 JSON 字符串（单题取第一项，多题为数组）
  const handleQAConfirm = (items) => {
    const serialized = JSON.stringify(items.length === 1 ? items[0] : items);
    onParamsChange && onParamsChange({ AnswerAssistMap: serialized });
    setQaDialogOpen(false);
  };

  // type=2/3 图片确认（保持数组，后端接收 ImageBase64List.N 列表）
  const handleImgConfirm = (base64List) => {
    onParamsChange && onParamsChange({ ImageBase64List: base64List });
    setImgDialogOpen(false);
  };

  return (
    <>
      <Form.Item className="column" label={params.label}>
        {/* 四宫格卡片 */}
        <div className="recognize-agent-task-type-list">
          {options.map((option) => (
            <div
              key={option.value}
              className={`recognize-agent-task-type-item ${value === option.value ? 'active' : ''}`}
              onClick={() => handleTypeChange(option.value)}
            >
              <span className="recognize-agent-task-type-item__title">{option.text}</span>
              {option.des && (
                <span className="recognize-agent-task-type-item__des">{option.des}</span>
              )}
            </div>
          ))}
        </div>

        {/* type=1：单题文本辅助入口 */}
        {value === 1 && (
          <div
            className={`assist-mark-entry ${qaCount > 0 ? 'assist-mark-entry--filled' : ''}`}
            onClick={() => setQaDialogOpen(true)}
          >
            <span className="assist-mark-entry__icon assist-mark-entry__icon--qa" />
            <span className="assist-mark-entry__title">单题文本辅助</span>
            {qaCount > 0
              ? <span className="assist-mark-entry__count">已录入 {qaCount} 题</span>
              : <span className="assist-mark-entry__placeholder">点击配置题干和答案</span>
            }
            <span className="assist-mark-entry__action">配置 &gt;</span>
          </div>
        )}

        {/* type=2/3：标准答案图片入口 */}
        {(value === 2 || value === 3) && (
          <div
            className={`assist-mark-entry ${imgCount > 0 ? 'assist-mark-entry--filled' : ''}`}
            onClick={() => setImgDialogOpen(true)}
          >
            <span className="assist-mark-entry__icon assist-mark-entry__icon--img" />
            <span className="assist-mark-entry__title">标准答案图片</span>
            {imgCount > 0
              ? <span className="assist-mark-entry__count">已上传 {imgCount} 张</span>
              : <span className="assist-mark-entry__placeholder">点击上传答案图片</span>
            }
            <span className="assist-mark-entry__action">配置 &gt;</span>
          </div>
        )}
      </Form.Item>

      {/* 单题文本辅助抽屉 */}
      {qaDialogOpen && (
        <AnswerAssistDialog
          visible={qaDialogOpen}
          initialItems={answerAssistItems.map((it) => ({
            Question: it.QuestionAuxStem || '',
            Answer: it.ReferenceAnswer || '',
            Analysis: it.Analysis || '',
            Score: it.Score != null ? String(it.Score) : '',
          }))}
          onConfirm={handleQAConfirm}
          onCancel={() => setQaDialogOpen(false)}
        />
      )}

      {/* 标准答案图片抽屉 */}
      {imgDialogOpen && (
        <ImageListDialog
          visible={imgDialogOpen}
          initialImages={Array.isArray(imageBase64List)
            ? imageBase64List.map((b64, i) => ({
                id: i,
                previewUrl: `data:image/jpeg;base64,${b64}`,
                base64: b64,
              }))
            : []
          }
          onConfirm={handleImgConfirm}
          onCancel={() => setImgDialogOpen(false)}
        />
      )}
    </>
  );
}

export default AssistMarkTypeRenderer;
